import { ChangeDetectionStrategy, Component, inject } from '@angular/core';

import { AlertController, RefresherCustomEvent } from '@ionic/angular';
import { select, Store } from '@ngrx/store';

import { Observable } from 'rxjs';

import { AddressEnum } from '../../enums/address';
import { PrimarySubscriptionPlanAccessAliasEnum } from '../../enums/monetization';
import { MonetizationService } from '../../misc/services/monetization.service';
import * as fromPropertyProfileActions from '../../store/actions/property-profile.actions';
import { PropertyProfileHistoryItem } from '../../store/models/property-profile.model';
import * as fromLayoutSelectors from '../../store/selectors/layout.selectors';
import * as fromPropertyProfileSelectors from '../../store/selectors/property-profile.selectors';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'red-property-search-history',
  standalone: false,
  templateUrl: './history.page.html'
})
export class HistoryPageComponent {
  private alertController = inject(AlertController);
  private monetizationService = inject(MonetizationService);
  private store = inject(Store);

  propertyProfileHistory$: Observable<PropertyProfileHistoryItem[]>;
  useMobileLayout$: Observable<boolean>;

  constructor() {
    this.propertyProfileHistory$ = this.store.pipe(select(fromPropertyProfileSelectors.selectHistory()));
    this.useMobileLayout$ = this.store.pipe(select(fromLayoutSelectors.selectUseMobileLayout));
  }

  propertyProfileHistoryListTrackBy(_index: number, propertyProfileHistoryItem: PropertyProfileHistoryItem) {
    return propertyProfileHistoryItem.id;
  }

  handleRefresh(event: RefresherCustomEvent) {
    this.store.dispatch(
      fromPropertyProfileActions.loadProfileHistory({
        payload: {
          ignoreLoader: true,
          refreshedCustomEvent: event
        }
      })
    );
  }

  async handleDelete(event: MouseEvent, propertyProfileHistoryItem: PropertyProfileHistoryItem) {
    event.preventDefault();
    event.stopImmediatePropagation();
    event.stopPropagation();
    const hasAccess = await this.monetizationService.accessFeature([PrimarySubscriptionPlanAccessAliasEnum.A]);
    if (hasAccess) {
      const alert = await this.alertController.create({
        buttons: [
          {
            role: 'cancel',
            text: 'Cancel'
          },
          {
            role: 'destructive',
            text: 'Delete'
          }
        ],
        header: 'Delete Profile',
        message: `Are you sure? You are about to delete a property profile from search history. This action cannot be undone.`
      });

      await alert.present();

      const { role } = await alert.onDidDismiss();
      if (role === 'destructive') {
        this.store.dispatch(
          fromPropertyProfileActions.deleteProfileHistoryItem({
            id: propertyProfileHistoryItem.id
          })
        );
      }
    }
  }

  async loadProfileFromHistory(propertyProfileHistoryItem: PropertyProfileHistoryItem) {
    const hasAccess = await this.monetizationService.accessFeature([PrimarySubscriptionPlanAccessAliasEnum.A]);
    if (hasAccess) {
      this.store.dispatch(
        fromPropertyProfileActions.loadProfileHistoryItem({
          payload: { id: propertyProfileHistoryItem.id }
        })
      );
    }
  }

  asPropertyProfileHistoryItem(propertyProfileHistoryItem: PropertyProfileHistoryItem) {
    return propertyProfileHistoryItem;
  }

  protected readonly addressType = AddressEnum;
}
